import React, { useState, useContext, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import LinearProgress from "@mui/material/LinearProgress";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";

// My Files
// import "../calendar/Calendar.css";
import { DatabaseContext, UserContext } from "../../context";
import UserAvatar from "../management/UserAvatar";
import firebase from "../../firebase";
import { jsonFromArray } from "../../utility";
import ShareDialogGroupHeader, {
    buildGroupCoverage,
    resolveGroupMembers,
    ShareDialogAvailableGroupRow,
    ShareDialogGroupIcon,
    ShareDialogGroupMembersDialog,
    ShareDialogGroupUserRow,
    ShareDialogPanelHeading,
    ShareDialogUserListLoading,
    ShareDialogUserSectionHeader,
    shareDialogSearchSx,
} from "../ShareDialogGroupHeader";
import ShareDialogDeliveryOptions from "../ShareDialogDeliveryOptions";
import ShareDialogDeliverySummary from "../ShareDialogDeliverySummary";
import ShareDialogSilentShareConfirm from "../ShareDialogSilentShareConfirm";
import useShareDialogUsersTabLoading from "../useShareDialogUsersTabLoading";
import {
    buildShareFeedbackMessage,
    buildSharedItemEmailHtml,
    sendSharedItemEmails,
} from "../shareDialogDelivery";

export default function SharePlaylistDialog(props) {
    const { enqueueSnackbar } = useSnackbar();
    const { users, groups, folders } = useContext(DatabaseContext);
    const { currentUserData } = useContext(UserContext);

    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedUserFilter, setSelectedUserFilter] = useState("");

    const [usernameFilter, setUsernameFilter] = useState("");

    const [selectedGroups, setSelectedGroups] = useState([]);
    const [selectedGroupFilter, setSelectedGroupFilter] = useState("");
    const [availableGroupFilter, setAvailableGroupFilter] = useState("");
    const [allHubs, setAllHubs] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    // The { group, hub } whose members are being viewed, or null
    const [viewingGroup, setViewingGroup] = useState(null);
    const { beginUsersTabLoading, usersTabLoading } =
        useShareDialogUsersTabLoading(activeTab);

    // In-platform notifications are the expected outcome of sharing, so they
    // start on and have to be deliberately turned off.
    const [sendNotifications, setSendNotifications] = useState(true);
    const [sendEmails, setSendEmails] = useState(false);
    const [embargoNotifications, setEmbargoNotifications] = useState(false);
    const notificationsMessageRef = useRef("");
    const [fromDate, setFromDate] = useState(new Date());
    const [silentShareConfirmOpen, setSilentShareConfirmOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const deliveryOptionsRef = useRef(null);
    const [saveError, setSaveError] = useState("");

    useEffect(() => {
        if (props.playlist) {
            // Populate selected users and groups
            let selectedUsersArray = [];
            if (props.playlist.users) {
                props.playlist.users.forEach((userKey) => {
                    let matchingUser = users.find((u) => u.key === userKey);
                    if (matchingUser) {
                        selectedUsersArray.push(matchingUser);
                    }
                });
            }

            setSelectedUsers(selectedUsersArray);

            let selectedGroupsArray = [];

            if (props.playlist.groups) {
                props.playlist.groups.forEach((groupKey) => {
                    let matchingGroup = groups.find((g) => g.key === groupKey);
                    if (matchingGroup) {
                        selectedGroupsArray.push(matchingGroup);
                    }
                });
            }

            setSelectedGroups(selectedGroupsArray);

            // Get the groups and users that should be visible (all those that can see the video file)
            let hubsArray = [];
            if (props.file.groups && allHubs.length === 0) {
                props.file.groups.forEach((groupKey) => {
                    let group = groups.find((g) => g.key === groupKey);
                    if (group) {
                        // console.log(group);

                        let hub = hubsArray.find((h) => h.key === group.hub);

                        if (hub) {
                            if (group.name === "All Users") {
                                group.displayName = "All in '" + hub.name + "'";
                            } else {
                                group.displayName = group.name;
                            }

                            hub.groups.push(group);
                        } else {
                            let folder = folders.find(
                                (f) => f.key === group.hub
                            );
                            if (folder) {
                                if (group.name === "All Users") {
                                    group.displayName =
                                        "All in '" + folder.name + "'";
                                } else {
                                    group.displayName = group.name;
                                }

                                hubsArray.push({
                                    key: group.hub,
                                    name: folder.name,
                                    ancestors: folder.ancestors || [],
                                    groups: [group],
                                });
                            }
                        }

                        // groupsArray.push(group);
                    }
                });
                setAllHubs(hubsArray);
            }
        }

        // eslint-disable-next-line
    }, [props.open]);

    const handleUserSelect = (user) => () => {
        let selectedArray = [...selectedUsers];
        selectedArray.push(user);
        setSelectedUsers(selectedArray);
        setUsernameFilter("");
    };

    const handleGroupSelect = (group) => () => {
        // Get the users from this group and remove them from the users which can be selected
        let selectedUsersArray = [...selectedUsers];

        group.users.forEach((user) => {
            let matchingUsers = users.filter((u) => u.key === user.key);

            if (matchingUsers.length === 1) {
                let userToSelect = matchingUsers[0];

                // Only add the user if they hadn't already been selected as an individual
                if (
                    selectedUsersArray.filter((u) => u.key === userToSelect.key)
                        .length === 0
                ) {
                    selectedUsersArray.push(userToSelect);
                }

                setSelectedUsers(selectedUsersArray);
            }
        });

        // Now select the group
        let selectedArray = [...selectedGroups];
        selectedArray.push(group);
        setSelectedGroups(selectedArray);
    };

    const handleDeleteUser = (user) => () => {
        let selectedArray = selectedUsers.filter((u) => u.key !== user.key);

        setSelectedUsers(selectedArray);
    };

    const handleDeleteGroup = (group) => () => {
        // Remove the users that were added from this group, unless they are also in a still selected group

        let selectedUsersArray = [...selectedUsers];

        group.users.forEach((user) => {
            let shouldRemove = true;

            selectedGroups.forEach((selectedGroup) => {
                if (selectedGroup.key !== group.key) {
                    if (
                        selectedGroup.users.filter((u) => u.key === user.key)
                            .length > 0
                    ) {
                        shouldRemove = false;
                    }
                }
            });

            if (shouldRemove) {
                selectedUsersArray = selectedUsersArray.filter(
                    (u) => u.key !== user.key
                );
            }
        });

        setSelectedUsers(selectedUsersArray);

        //
        let selectedArray = selectedGroups.filter((u) => u.key !== group.key);
        setSelectedGroups(selectedArray);
    };

    function handleCancelPress() {
        if (isSaving) {
            return;
        }
        setUsernameFilter("");
        setSelectedUserFilter("");
        setSelectedGroupFilter("");
        setAvailableGroupFilter("");
        setActiveTab(0);
        notificationsMessageRef.current = "";
        setSendNotifications(true);
        setSendEmails(false);
        setEmbargoNotifications(false);
        setSilentShareConfirmOpen(false);
        setFromDate(new Date());
        setSaveError("");
        setSelectedGroups([]);
        setSelectedUsers([]);
        setViewingGroup(null);
        props.onClose();
    }

    async function handleConfirmPress() {
        const groupsObject = jsonFromArray(selectedGroups);
        const usersObject = {};

        selectedUsers.forEach((user) => {
            if (!isUserInSelectedGroups(user)) {
                usersObject[user.key] = true;
            }
        });

        const notificationDate = embargoNotifications ? fromDate : new Date();
        const title = props.playlist.name || "Untitled playlist";
        const tasks = [
            firebase
                .database()
                .ref("playlists")
                .child(props.playlist.key)
                .update({ users: usersObject, groups: groupsObject }),
            firebase
                .database()
                .ref("playlistinfo")
                .child(props.playlist.key)
                .update({ users: usersObject, groups: groupsObject }),
            firebase.database().ref("fileusage").push({
                file: props.file.key,
                fileName: props.file.displayName,
                fileType: props.file.type,
                user: currentUserData.key,
                action: "PlaylistShare",
                usersSharedWith: usersObject,
                groupsSharedWith: groupsObject,
                date: dayjs().toString(),
            }),
        ];

        if (sendNotifications && recipients.length > 0) {
            tasks.push(
                firebase
                    .database()
                    .ref("notifications")
                    .child(props.playlist.key)
                    .set({
                        file: props.playlist.video,
                        title,
                        type: "playlist",
                        users: jsonFromArray(recipients),
                        message: notificationsMessageRef.current,
                        date: dayjs().toString(),
                        fromDate: dayjs(notificationDate).toString(),
                    }),
            );
        }

        if (sendEmails) {
            const html = buildSharedItemEmailHtml({
                actionLabel: "View playlist",
                assetType: "playlist",
                link:
                    "https://nodiant.faw.cymru/playlist?key=" +
                    encodeURIComponent(props.playlist.key),
                message: notificationsMessageRef.current,
                title,
            });
            tasks.push(
                sendSharedItemEmails({
                    embargoNotifications,
                    firebase,
                    html,
                    label: `playlist: ${title}`,
                    notificationDate,
                    recipients,
                    subject: "Nodiant Online - New Playlist Available",
                }),
            );
        }

        setIsSaving(true);
        setSaveError("");

        try {
            await Promise.all(tasks);

            enqueueSnackbar(
                buildShareFeedbackMessage({
                    assetLabel: "Playlist",
                    userCount: selectedUsers.length,
                    groupCount: selectedGroups.length,
                    sendNotifications,
                    sendEmails,
                    embargoNotifications,
                    notificationDate,
                }),
                {
                    variant: "success",
                    autoHideDuration: 4500,
                },
            );

            const userCount = selectedUsers.length;
            setUsernameFilter("");
            setSelectedUserFilter("");
            setSelectedGroupFilter("");
            setAvailableGroupFilter("");
            setActiveTab(0);
            notificationsMessageRef.current = "";
            setSendNotifications(true);
            setSendEmails(false);
            setEmbargoNotifications(false);
            setSilentShareConfirmOpen(false);
            setFromDate(new Date());
            setSelectedGroups([]);
            setSelectedUsers([]);
            setViewingGroup(null);
            setIsSaving(false);
            props.onClose(userCount);
        } catch (error) {
            console.error("Unable to share playlist", error);
            setSaveError(
                "The playlist could not be shared. Please check your connection and try again.",
            );
            setIsSaving(false);
        }
    }

    const handleChangeFilterText = (event) => {
        setUsernameFilter(event.target.value);
    };

    function isUserInSelectedGroups(user) {
        for (let i = 0; i < selectedGroups.length; i++) {
            let group = selectedGroups[i];
            for (let j = 0; j < group.users.length; j++) {
                let u = group.users[j];

                if (user.key === u.key) {
                    return true;
                }
            }
        }
        return false;
    }

    function groupLabel(group) {
        return `${groupName(group)} (${groupMemberLabel(group)})`;
    }

    function groupName(group) {
        return group.displayName || group.name;
    }

    function groupMemberLabel(group) {
        return `${group.users.length} ${
            group.users.length === 1 ? "User" : "Users"
        }`;
    }

    function userShouldBeVisible(user) {
        if (currentUserData) {
            if (user.key === currentUserData.key) {
                return false;
            }

            var hasPermissionToSee = false;

            if (props.file.managers && props.file.managers.includes(user.key)) {
                hasPermissionToSee = true;
            }

            if (user.files.includes(props.file.key)) {
                hasPermissionToSee = true;
            }

            allHubs.forEach((hub) => {
                hub.groups.forEach((group) => {
                    if (
                        user.firstName === "Matthew" &&
                        user.lastName === "Parry"
                    ) {
                    }

                    if (
                        group.users.filter((u) => u.key === user.key).length > 0
                    ) {
                        hasPermissionToSee = true;
                    }
                });
            });

            if (hasPermissionToSee) {
                if (
                    selectedUsers.filter((u) => u.key === user.key).length === 0
                ) {
                    if (
                        user.fullName
                            .toLowerCase()
                            .includes(usernameFilter.toLowerCase())
                    ) {
                        return true;
                    }
                }
            }
        }

        return false;
    }

    function handleDeliveryChange(event) {
        const { checked, name } = event.target;

        if (name === "notification") {
            setSendNotifications(checked);
        } else if (name === "email") {
            setSendEmails(checked);
        } else if (name === "embargo") {
            setEmbargoNotifications(checked);
            setFromDate(new Date());
        }

        if (
            (name === "notification" && !checked && !sendEmails) ||
            (name === "email" && !checked && !sendNotifications)
        ) {
            setEmbargoNotifications(false);
            setFromDate(new Date());
        }
    }

    const handleChangeMessageText = (value) => {
        notificationsMessageRef.current = value;
    };

    const sortedUsers = (usersToSort) =>
        [...usersToSort].sort((a, b) =>
            a.lastName.toLowerCase() > b.lastName.toLowerCase() ? 1 : -1
        );

    const directlySelectedUsers = selectedUsers.filter(
        (user) => !isUserInSelectedGroups(user)
    );

    const groupCoverage = buildGroupCoverage({
        getGroupName: groupName,
        selectedGroups,
        users,
    });

    const viewedGroupMembers = resolveGroupMembers(viewingGroup?.group, users);

    const handleViewGroupMembers = (group, hub) => () => {
        setViewingGroup({ group, hub });
    };

    const handleGroupMembersClose = () => {
        setViewingGroup(null);
    };

    const recipientByKey = new Map(
        selectedUsers.map((user) => [user.key, user]),
    );
    selectedGroups.forEach((group) => {
        group.users.forEach((groupUser) => {
            const matchingUser = users.find(
                (user) => user.key === groupUser.key,
            );
            if (matchingUser) {
                recipientByKey.set(matchingUser.key, matchingUser);
            }
        });
    });
    recipientByKey.delete(currentUserData?.key);
    const recipients = Array.from(recipientByKey.values());

    // Nobody being told is only a mistake worth interrupting for when there is
    // somebody to tell.
    const wouldShareSilently =
        recipients.length > 0 && !sendNotifications && !sendEmails;

    function handleReviewDeliveryOptions() {
        deliveryOptionsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
    }

    function handleShareClick() {
        if (wouldShareSilently) {
            setSilentShareConfirmOpen(true);
            return;
        }

        handleConfirmPress();
    }

    function handleSilentShareCancel() {
        setSilentShareConfirmOpen(false);
        handleReviewDeliveryOptions();
    }

    function handleSilentShareConfirm() {
        setSilentShareConfirmOpen(false);
        handleConfirmPress();
    }

    const shouldPrepareUserLists = activeTab === 1 && !usersTabLoading;
    const availableUsers = shouldPrepareUserLists
        ? users.filter((user) => userShouldBeVisible(user))
        : [];
    const filteredSelectedUsers = shouldPrepareUserLists
        ? sortedUsers(directlySelectedUsers).filter((user) =>
              user.fullName
                  .toLowerCase()
                  .includes(selectedUserFilter.toLowerCase()),
          )
        : [];
    const filteredGroupSelectedUsers = shouldPrepareUserLists
        ? groupCoverage.members.filter((user) =>
              (user.fullName || "")
                  .toLowerCase()
                  .includes(selectedUserFilter.toLowerCase()),
          )
        : [];

    const selectedUserSections = [
        {
            key: "individual",
            label: "Added individually",
            icon: <PersonOutlineIcon sx={{ fontSize: 17, flexShrink: 0 }} />,
            users: filteredSelectedUsers,
            viaGroup: false,
        },
        {
            key: "viaGroups",
            label: "Included by a group",
            icon: <GroupsOutlinedIcon sx={{ fontSize: 17, flexShrink: 0 }} />,
            users: filteredGroupSelectedUsers,
            viaGroup: true,
        },
    ].filter((section) => section.users.length > 0);

    // With only one section on show its header would just repeat the panel title
    const showSelectedUserSectionHeaders = selectedUserSections.length > 1;

    const handleTabChange = (event, value) => {
        if (value === 1 && activeTab !== 1) {
            beginUsersTabLoading();
        }
        setActiveTab(value);
    };
    const sortedHubs = [...allHubs].sort(
        (first, second) =>
            (first.ancestors?.length || 0) -
            (second.ancestors?.length || 0),
    );

    const getHubBreadcrumb = (hub) => {
        if (!hub) return "Hub unavailable";

        const ancestorNames = (hub.ancestors || [])
            .map((ancestorKey) =>
                folders.find((folder) => folder.key === ancestorKey),
            )
            .filter(Boolean)
            .sort(
                (first, second) =>
                    (first.ancestors?.length || 0) -
                    (second.ancestors?.length || 0),
            )
            .map((ancestorHub) => ancestorHub.name);

        return [...ancestorNames, hub.name].join(" › ");
    };

    const selectedGroupOptions = selectedGroups
        .map((group) => ({
            group,
            hub: sortedHubs.find((hub) =>
                hub.groups.some((hubGroup) => hubGroup.key === group.key),
            ),
        }))
        .filter(({ group, hub }) =>
            `${groupLabel(group)} ${getHubBreadcrumb(hub)}`
                .toLowerCase()
                .includes(selectedGroupFilter.toLowerCase()),
        )
        .sort((first, second) => {
            const hubComparison = getHubBreadcrumb(first.hub).localeCompare(
                getHubBreadcrumb(second.hub),
            );
            return (
                hubComparison ||
                groupLabel(first.group).localeCompare(groupLabel(second.group))
            );
        });

    const availableHubs = sortedHubs
        .map((hub) => ({
            ...hub,
            groups: hub.groups
                .filter(
                    (group) =>
                        !selectedGroups.some(
                            (selectedGroup) =>
                                selectedGroup.key === group.key,
                        ) &&
                        `${groupLabel(group)} ${getHubBreadcrumb(hub)}`
                            .toLowerCase()
                            .includes(availableGroupFilter.toLowerCase()),
                )
                .sort((first, second) =>
                    groupLabel(first).localeCompare(groupLabel(second)),
                ),
        }))
        .filter((hub) => hub.groups.length > 0);

    const surfaceSx = {
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        overflow: "hidden",
    };

    return (
        <Dialog
            open={props.open}
            onClose={handleCancelPress}
            fullWidth
            maxWidth="lg"
            aria-labelledby="share-playlist-dialog-title"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                        maxHeight: "92vh",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <GroupsOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="share-playlist-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Share playlist
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Choose the groups and individual users who can access
                        this playlist.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Cancel sharing changes"
                    onClick={handleCancelPress}
                    disabled={isSaving}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                {saveError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {saveError}
                    </Alert>
                )}

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 1,
                        mb: 2,
                    }}
                >
                    {[
                        {
                            icon: <GroupsOutlinedIcon />,
                            label: "Groups",
                            value: selectedGroups.length,
                            // Counted across groups, so anyone in more than one
                            // selected group is only counted once
                            caption: `Containing ${
                                groupCoverage.memberKeys.size
                            } ${
                                groupCoverage.memberKeys.size === 1
                                    ? "user"
                                    : "users"
                            }`,
                        },
                        {
                            icon: <PersonOutlineIcon />,
                            label: "Individual users",
                            value: directlySelectedUsers.length,
                        },
                        {
                            icon: <ShareOutlinedIcon />,
                            label: "Recipients",
                            value: recipients.length,
                        },
                    ].map((summary) => (
                        <Box
                            key={summary.label}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.25,
                                p: 1.5,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                backgroundColor: "background.paper",
                            }}
                        >
                            <Box sx={{ color: "primary.main", display: "grid" }}>
                                {summary.icon}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {summary.label}
                                </Typography>
                                <Typography variant="h6" sx={{ lineHeight: 1 }}>
                                    {summary.value}
                                </Typography>
                                {summary.caption && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.4 }}
                                    >
                                        {summary.caption}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>

                <Box sx={surfaceSx}>
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 800 }}
                        >
                            People with access
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Everyone listed can access this playlist.
                        </Typography>
                    </Box>
                    <Divider />
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                    >
                        <Tab
                            icon={<GroupsOutlinedIcon />}
                            iconPosition="start"
                            label={`Groups (${selectedGroups.length})`}
                        />
                        <Tab
                            icon={<PersonOutlineIcon />}
                            iconPosition="start"
                            label={`Users (${directlySelectedUsers.length})`}
                        />
                    </Tabs>

                    <Box
                        sx={{
                            p: { xs: 1.5, sm: 2 },
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "minmax(0, 1fr)",
                                md: "repeat(2, minmax(0, 1fr))",
                            },
                            gap: 2,
                            backgroundColor: "background.subtle",
                        }}
                    >
                        {activeTab === 0 ? (
                            <>
                                <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.5, sm: 2 } }}>
                                    <ShareDialogPanelHeading
                                        title="Selected groups"
                                        count={selectedGroups.length}
                                    />
                                    <TextField
                                        label="Search selected groups"
                                        size="small"
                                        fullWidth
                                        value={selectedGroupFilter}
                                        onChange={(event) =>
                                            setSelectedGroupFilter(event.target.value)
                                        }
                                        sx={shareDialogSearchSx}
                                    />
                                    <List
                                        disablePadding
                                        sx={{
                                            height: { md: 390 },
                                            maxHeight: { xs: 300, md: 390 },
                                            overflowY: "auto",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                    >
                                        {selectedGroupOptions.length === 0 ? (
                                            <ListItem>
                                                <ListItemText
                                                    primary="No selected groups"
                                                    primaryTypographyProps={{
                                                        color: "text.secondary",
                                                        variant: "body2",
                                                    }}
                                                />
                                            </ListItem>
                                        ) : (
                                            selectedGroupOptions.map(({ group, hub }, index) => (
                                                <React.Fragment key={group.key}>
                                                    {(index === 0 ||
                                                        selectedGroupOptions[index - 1].hub?.key !==
                                                            hub?.key) && (
                                                        <ShareDialogGroupHeader
                                                            breadcrumb={getHubBreadcrumb(hub)}
                                                        />
                                                    )}
                                                    <ListItem
                                                        disablePadding
                                                        secondaryAction={
                                                            <IconButton
                                                                aria-label={`Remove ${groupLabel(group)}`}
                                                                onClick={handleDeleteGroup(group)}
                                                                size="small"
                                                                sx={{ color: "error.main" }}
                                                            >
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        }
                                                    >
                                                        <ListItemButton
                                                            onClick={handleViewGroupMembers(
                                                                group,
                                                                hub,
                                                            )}
                                                            aria-label={`View the members of ${groupName(group)}`}
                                                            sx={{
                                                                gap: 1,
                                                                px: 1.25,
                                                                py: 0.85,
                                                            }}
                                                        >
                                                            <ShareDialogGroupIcon />
                                                            <ListItemText
                                                                primary={groupName(group)}
                                                                secondary={groupMemberLabel(group)}
                                                                primaryTypographyProps={{
                                                                    variant: "body2",
                                                                    fontWeight: 800,
                                                                    noWrap: true,
                                                                }}
                                                                secondaryTypographyProps={{
                                                                    variant: "caption",
                                                                    noWrap: true,
                                                                }}
                                                            />
                                                        </ListItemButton>
                                                    </ListItem>
                                                    {index < selectedGroupOptions.length - 1 &&
                                                        selectedGroupOptions[index + 1].hub?.key ===
                                                            hub?.key && <Divider />}
                                                </React.Fragment>
                                            ))
                                        )}
                                    </List>
                                </Paper>

                                <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.5, sm: 2 } }}>
                                    <ShareDialogPanelHeading
                                        title="Available groups"
                                        count={availableHubs.reduce(
                                            (count, hub) => count + hub.groups.length,
                                            0,
                                        )}
                                    />
                                    <TextField
                                        label="Search available groups"
                                        size="small"
                                        fullWidth
                                        value={availableGroupFilter}
                                        onChange={(event) =>
                                            setAvailableGroupFilter(event.target.value)
                                        }
                                        sx={shareDialogSearchSx}
                                    />
                                    <List
                                        disablePadding
                                        sx={{
                                            height: { md: 390 },
                                            maxHeight: { xs: 300, md: 390 },
                                            overflowY: "auto",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                    >
                                        {availableHubs.length === 0 ? (
                                            <ListItem>
                                                <ListItemText
                                                    primary="No matching groups are available"
                                                    primaryTypographyProps={{
                                                        color: "text.secondary",
                                                        variant: "body2",
                                                    }}
                                                />
                                            </ListItem>
                                        ) : (
                                            availableHubs.map((hub) => (
                                                <React.Fragment key={hub.key}>
                                                    <ShareDialogGroupHeader
                                                        breadcrumb={getHubBreadcrumb(hub)}
                                                    />
                                                    {hub.groups.map((group, index) => (
                                                        <React.Fragment key={group.key}>
                                                            <ShareDialogAvailableGroupRow
                                                                name={groupName(group)}
                                                                memberCount={group.users.length}
                                                                onClick={handleGroupSelect(group)}
                                                                divided={
                                                                    index < hub.groups.length - 1
                                                                }
                                                            />
                                                        </React.Fragment>
                                                    ))}
                                                </React.Fragment>
                                            ))
                                        )}
                                    </List>
                                </Paper>
                            </>
                        ) : (
                            <>
                                <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.5, sm: 2 } }}>
                                    <ShareDialogPanelHeading
                                        title="Selected users"
                                        count={
                                            usersTabLoading
                                                ? "—"
                                                : directlySelectedUsers.length
                                        }
                                    />
                                    <TextField
                                        label="Search selected users"
                                        size="small"
                                        fullWidth
                                        value={selectedUserFilter}
                                        disabled={usersTabLoading}
                                        onChange={(event) =>
                                            setSelectedUserFilter(event.target.value)
                                        }
                                        sx={shareDialogSearchSx}
                                    />
                                    {usersTabLoading ? (
                                        <ShareDialogUserListLoading label="Loading selected users…" />
                                    ) : (
                                    <List
                                        disablePadding
                                        sx={{
                                            height: { md: 390 },
                                            maxHeight: { xs: 300, md: 390 },
                                            overflowY: "auto",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                    >
                                        {selectedUserSections.length === 0 ? (
                                            <ListItem>
                                                <ListItemText
                                                    primary="No users have been added individually"
                                                    primaryTypographyProps={{
                                                        color: "text.secondary",
                                                        variant: "body2",
                                                    }}
                                                />
                                            </ListItem>
                                        ) : (
                                            selectedUserSections.map((section) => (
                                                <React.Fragment key={section.key}>
                                                    {showSelectedUserSectionHeaders && (
                                                        <ShareDialogUserSectionHeader
                                                            icon={section.icon}
                                                            label={section.label}
                                                            count={section.users.length}
                                                        />
                                                    )}
                                                    {section.users.map((user, index) =>
                                                        section.viaGroup ? (
                                                            <ShareDialogGroupUserRow
                                                                key={user.key}
                                                                user={user}
                                                                groupNames={groupCoverage.groupNamesByUserKey.get(
                                                                    user.key,
                                                                )}
                                                                divided={
                                                                    index < section.users.length - 1
                                                                }
                                                            />
                                                        ) : (
                                                            <React.Fragment key={user.key}>
                                                                <ListItem
                                                                    sx={{
                                                                        gap: 1,
                                                                        px: 1.25,
                                                                        py: 0.65,
                                                                    }}
                                                                    secondaryAction={
                                                                        user.key !== currentUserData?.key && (
                                                                            <IconButton
                                                                                aria-label={`Remove ${user.fullName}`}
                                                                                onClick={handleDeleteUser(user)}
                                                                                size="small"
                                                                                sx={{ color: "error.main" }}
                                                                            >
                                                                                <DeleteOutlineIcon fontSize="small" />
                                                                            </IconButton>
                                                                        )
                                                                    }
                                                                >
                                                                    <ListItemAvatar>
                                                                        <UserAvatar
                                                                            userOrHub={user}
                                                                            path="users"
                                                                            variant="circular"
                                                                            size="small"
                                                                        />
                                                                    </ListItemAvatar>
                                                                    <ListItemText
                                                                        primary={user.fullName}
                                                                        secondary={user.customRoleName}
                                                                        primaryTypographyProps={{
                                                                            variant: "body2",
                                                                            fontWeight: 800,
                                                                        }}
                                                                    />
                                                                </ListItem>
                                                                {index < section.users.length - 1 && (
                                                                    <Divider />
                                                                )}
                                                            </React.Fragment>
                                                        ),
                                                    )}
                                                </React.Fragment>
                                            ))
                                        )}
                                    </List>
                                    )}
                                </Paper>

                                <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.5, sm: 2 } }}>
                                    <ShareDialogPanelHeading
                                        title="Available users"
                                        count={
                                            usersTabLoading
                                                ? "—"
                                                : availableUsers.length
                                        }
                                    />
                                    <TextField
                                        label="Search available users"
                                        size="small"
                                        fullWidth
                                        value={usernameFilter}
                                        disabled={usersTabLoading}
                                        onChange={handleChangeFilterText}
                                        sx={shareDialogSearchSx}
                                    />
                                    {usersTabLoading ? (
                                        <ShareDialogUserListLoading label="Loading available users…" />
                                    ) : (
                                    <List
                                        disablePadding
                                        sx={{
                                            height: { md: 390 },
                                            maxHeight: { xs: 300, md: 390 },
                                            overflowY: "auto",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                    >
                                        {availableUsers.length === 0 ? (
                                            <ListItem>
                                                <ListItemText
                                                    primary="No matching users are available"
                                                    primaryTypographyProps={{
                                                        color: "text.secondary",
                                                        variant: "body2",
                                                    }}
                                                />
                                            </ListItem>
                                        ) : (
                                            availableUsers.map((user, index) => (
                                                <React.Fragment key={user.key}>
                                                    <ListItemButton
                                                        onClick={handleUserSelect(user)}
                                                        sx={{
                                                            gap: 1,
                                                            px: 1.25,
                                                            py: 0.65,
                                                        }}
                                                    >
                                                        <ListItemAvatar>
                                                            <UserAvatar
                                                                userOrHub={user}
                                                                path="users"
                                                                variant="circular"
                                                                size="small"
                                                            />
                                                        </ListItemAvatar>
                                                        <ListItemText
                                                            primary={user.fullName}
                                                            secondary={user.customRoleName}
                                                            primaryTypographyProps={{ variant: "body2" }}
                                                        />
                                                        <AddCircleOutlineIcon
                                                            fontSize="small"
                                                            sx={{ color: "primary.main" }}
                                                        />
                                                    </ListItemButton>
                                                    {index < availableUsers.length - 1 && <Divider />}
                                                </React.Fragment>
                                            ))
                                        )}
                                    </List>
                                    )}
                                </Paper>
                            </>
                        )}
                    </Box>
                </Box>

                <Box sx={{ mt: 2 }} ref={deliveryOptionsRef}>
                    <ShareDialogDeliveryOptions
                        assetType="playlist"
                        sendNotifications={sendNotifications}
                        sendEmails={sendEmails}
                        embargoNotifications={embargoNotifications}
                        message={notificationsMessageRef.current}
                        messageResetKey={props.open}
                        fromDate={fromDate}
                        onDeliveryChange={handleDeliveryChange}
                        onMessageChange={handleChangeMessageText}
                        onDateChange={setFromDate}
                    />
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    gap: 1,
                    flexWrap: "wrap",
                }}
            >
                <ShareDialogDeliverySummary
                    sendNotifications={sendNotifications}
                    sendEmails={sendEmails}
                    embargoNotifications={embargoNotifications}
                    fromDate={fromDate}
                    recipientCount={recipients.length}
                    onReview={handleReviewDeliveryOptions}
                />
                <Button
                    onClick={handleCancelPress}
                    color="inherit"
                    disabled={isSaving}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleShareClick}
                    variant="contained"
                    disableElevation
                    startIcon={<ShareOutlinedIcon />}
                    disabled={isSaving}
                >
                    {isSaving ? "Sharing…" : "Share playlist"}
                </Button>
            </DialogActions>
            {isSaving && <LinearProgress />}

            <ShareDialogGroupMembersDialog
                open={viewingGroup !== null}
                groupName={
                    viewingGroup ? groupName(viewingGroup.group) : ""
                }
                breadcrumb={
                    viewingGroup ? getHubBreadcrumb(viewingGroup.hub) : ""
                }
                members={viewedGroupMembers}
                onClose={handleGroupMembersClose}
            />

            <ShareDialogSilentShareConfirm
                open={silentShareConfirmOpen}
                assetType="playlist"
                recipientCount={recipients.length}
                onCancel={handleSilentShareCancel}
                onConfirm={handleSilentShareConfirm}
            />
        </Dialog>
    );
}
